import { isEmptyCategories } from '@repo/shared/dtos/task'
import { Button, ButtonProps, ButtonVariant } from '@repo/ui/button'
import { getLocale, useT } from '@repo/ui/hooks'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { cn, formatDate } from '@repo/ui/utils'
import { addDays, format, isBefore, isSameDay, startOfDay } from 'date-fns'
import {
  Calendar,
  CalendarClock,
  CalendarX,
  Circle,
  CircleCheckBig,
  Link,
  Trash2,
  Undo2,
} from 'lucide-react'
import { useEffect, useState } from 'react'

import { DatePickerWithPresets } from '@/components/DatePickerWithPressets'
import { Task } from '@/store/task'

export function TaskDueDate({
  className,
  initialDueDate,
  isEditable = false,
  onDueDateChange,
}: {
  className?: string
  initialDueDate?: Task['dueDate']
  isEditable?: boolean
  onDueDateChange?: (date: Date | undefined) => void
  taskId?: Task['id']
}) {
  const { tc } = useT()
  const [date, setDate] = useState<Date | undefined>()

  useEffect(() => {
    setDate(initialDueDate ? startOfDay(initialDueDate) : undefined)
  }, [initialDueDate])

  const today = startOfDay(new Date())
  const isToDay = !!(date && isSameDay(date, today))
  const isTomorrow = !!(date && isSameDay(date, addDays(today, 1)))
  const isInAWeek = !!(date && isSameDay(date, addDays(today, 7)))
  const isOverDue = !!(date && isBefore(date, today))

  const updateDate = (date: Date | undefined) => {
    setDate(date)
    onDueDateChange?.(date)
  }
  return (
    <DatePickerWithPresets
      isDisabled={!isEditable}
      dateHandler={{
        date,
        setDate: updateDate,
      }}
    >
      <div
        className={cn(
          'flex w-fit select-none items-center gap-1 text-sm',
          isEditable &&
            'data-[state=open]:text-primary text-primary-500 hover:text-primary cursor-pointer underline hover:underline data-[state=open]:underline',
          isEditable && isToDay
            ? 'text-highlight data-[state=open]:text-highlight'
            : isEditable && isOverDue && 'text-red-500 data-[state=open]:text-red-500',
          className,
        )}
      >
        {date ? (
          <TooltipWrapper content={format(date, 'PPPP', { locale: getLocale() })}>
            <div className="flex items-center gap-1">
              {isEditable && isToDay ? (
                <CalendarClock className="size-4" />
              ) : isEditable && isOverDue ? (
                <CalendarX className="size-4" />
              ) : (
                <Calendar className="size-4" />
              )}
              {isToDay
                ? tc('today')
                : isTomorrow
                  ? tc('tomorrow')
                  : isInAWeek
                    ? tc('inAWeek')
                    : formatDate(date)}
            </div>
          </TooltipWrapper>
        ) : (
          <>
            <Calendar className="size-4" />
            {tc('noDueDate')}
          </>
        )}
      </div>
    </DatePickerWithPresets>
  )
}

export function TaskCategories({ task, className }: { task: Task; className?: string }) {
  if (!task.categories || isEmptyCategories(task.categories)) return null
  return (
    <div className={cn('flex w-fit select-none items-start justify-center gap-1', className)}>
      <div className="flex flex-wrap gap-1">
        {task.categories.map((v, i) => (
          <div
            key={i}
            className="flex h-full w-fit items-center text-pretty rounded bg-neutral-100 px-2 py-px text-xs uppercase text-gray-600"
          >
            {v}
          </div>
        ))}
      </div>
    </div>
  )
}

export function TaskRestorable({ task, onClick }: { task: Task; onClick: (id: string) => void }) {
  const { tc } = useT()
  return (
    <div className="flex items-center justify-center">
      <TooltipWrapper content={tc('restore', { cfl: true })}>
        <Button
          type="button"
          variant="link"
          onClick={() => onClick(task.id)}
          size="icon"
          className="text-primary-400 transition-colors hover:text-green-500"
          tabIndex={-1}
        >
          <Undo2 className="size-4" />
        </Button>
      </TooltipWrapper>
    </div>
  )
}

export function TaskDeletable({ task, onClick }: { task: Task; onClick: (id: string) => void }) {
  const { tc } = useT()
  return (
    <Button variant="outline" size="sm" isSensitive onClick={() => onClick(task.id)}>
      <Trash2 className="size-4" />
      {tc('delete', { cfl: true })}
    </Button>
  )
}

export function TaskDoable({
  className,
  isDone = false,
  isLabelShown = false,
  onClick,
  size = 'icon',
  task,
  variant = 'link',
  withTooltip = true,
}: {
  className?: string
  isDone?: boolean
  isLabelShown?: boolean
  onClick: (id: string) => void
  size?: ButtonProps['size']
  task: Task
  variant?: ButtonVariant
  withTooltip?: boolean
}) {
  const { tc } = useT()
  const [hovered, setHovered] = useState(false)
  const LabelComponent = (
    <span>{isDone ? tc('restore', { cfl: true }) : tc('done', { cfl: true })}</span>
  )
  const button = (
    <Button
      variant={variant}
      size={size}
      className={cn(
        isDone ? 'min-h-full text-green-500 ' : 'text-primary-300 min-h-full  hover:text-green-500',
        className,
      )}
      onClick={() => onClick(task.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {isDone ? (
        <Undo2 className="size-4" />
      ) : hovered ? (
        <CircleCheckBig className="size-4" />
      ) : (
        <Circle className="size-4" />
      )}
      {isLabelShown && LabelComponent}
    </Button>
  )
  if (!withTooltip) return button
  return isDone ? (
    <TooltipWrapper content={tc('undo', { cfl: true })}>{button}</TooltipWrapper>
  ) : (
    <TooltipWrapper content={tc('done', { cfl: true })}>{button}</TooltipWrapper>
  )
}

export function TaskLink({ className, task }: { className?: string; task: Task }) {
  if (
    'cta' in task &&
    task.cta !== null &&
    typeof task.cta === 'object' &&
    'name' in task.cta &&
    'link' in task.cta &&
    typeof task.cta.link === 'string' &&
    typeof task.cta.name === 'string' &&
    task.cta.link.startsWith('https://')
  )
    return (
      <Button variant="link" size="sm" className={cn('w-fit', className)} asChild>
        <a
          href={task.cta.link}
          target="_blank"
          className="flex min-h-fit max-w-fit flex-row flex-wrap items-center justify-between gap-2 text-pretty py-2"
        >
          <Link className="size-3.5" />
          {task.cta.name}
        </a>
      </Button>
    )
}
